import { Reference } from 'projects/reference-management/src/lib/reference.model';
import { getReferenceDisplayValue } from 'projects/reference-management/src/lib/references/reference-display.utils';
import { getReferenceReviewed, getReferenceSourceClass } from 'projects/reference-management/src/lib/references/reference-source.utils';
import { normalizeText } from '../utils/normalize';
import {
  BibliographyReferenceOccurrenceVm,
  BibliographyReferenceRowVm,
} from './reference-table.models';

interface BuildBibliographyReferenceRowInput {
  reference: Reference;
  citedIn: BibliographyReferenceOccurrenceVm[];
  rowIndex: number;
  showEdit: boolean;
}

/**
 * Collects all valid identifiers that can address a reference.
 */
export const getReferenceIds = (reference: Reference): string[] => {
  const ids = [reference?.csl?.id, reference?.key]
    .map((value) => normalizeText(value))
    .filter(Boolean) as string[];
  return Array.from(new Set(ids));
};

/**
 * Resolves the primary identifier used for row actions.
 */
export const getPrimaryReferenceId = (reference: Reference): string | undefined => {
  const ids = getReferenceIds(reference);
  return ids[0];
};

/**
 * Resolves the display title for a bibliography reference row.
 */
export const buildReferenceTitle = (reference: Reference): string => {
  return (
    resolveReferenceDisplayText(reference) ||
    normalizeText(reference?.csl?.title) ||
    normalizeText(reference?.bib) ||
    'Reference'
  );
};

/**
 * Resolves the display text for plain references.
 */
export const resolveReferenceDisplayText = (reference: Reference): string => {
  return normalizeText(getReferenceDisplayValue(reference)) || '';
};

/**
 * Builds one reducer-backed bibliography row view model.
 */
export const buildBibliographyReferenceRow = ({
  reference,
  citedIn,
  rowIndex,
  showEdit,
}: BuildBibliographyReferenceRowInput): BibliographyReferenceRowVm => {
  const refId = getPrimaryReferenceId(reference);
  const customText = normalizeText(reference?.csl?.rawCitation);

  return {
    refId,
    referenceKey: reference.key,
    title: buildReferenceTitle(reference),
    customText,
    meta: buildRowMeta(reference),
    missingMetadata: buildMissingMetadata(reference),
    source: getReferenceSourceClass(reference),
    exportFormat: customText ? 'custom' : 'auto',
    citedIn,
    canEdit: Boolean(showEdit && refId),
    isPlainReference: !reference?.csl,
    reviewed: getReferenceReviewed(reference),
    rowIndex,
  };
};

/**
 * Builds the metadata line shown below the title.
 */
export const buildRowMeta = (reference: Reference): string | undefined => {
  const csl = reference?.csl as Record<string, unknown> | undefined;
  if (!csl) {
    return undefined;
  }

  const metaParts: string[] = [];
  const authors = getAuthors(csl);
  if (authors.length > 0) {
    metaParts.push(authors.join(' · '));
  }

  const source = normalizeText(
    Array.isArray(csl['container-title']) ? csl['container-title'][0] : csl['container-title'],
  );
  if (source) {
    metaParts.push(source);
  }

  const year = getYear(csl);
  if (year) {
    metaParts.push(year);
  }

  if (metaParts.length === 0) {
    return undefined;
  }

  return metaParts.join(' · ');
};

/**
 * Extracts compact author labels for the metadata line.
 */
export const getAuthors = (csl: Record<string, unknown>): string[] => {
  const authors = Array.isArray(csl.author) ? csl.author : [];
  return authors
    .map((author) => formatAuthor(author as Record<string, unknown>))
    .filter((value): value is string => Boolean(value));
};

/**
 * Formats one CSL author into a compact bibliography label.
 */
export const formatAuthor = (author: Record<string, unknown>): string | undefined => {
  const literal = normalizeText(author.literal);
  if (literal) {
    return literal;
  }

  const family = normalizeText(author.family);
  const given = normalizeText(author.given);

  if (family && given) {
    const firstGiven = given.split(/\s+/)[0];
    const initial = firstGiven ? `${firstGiven.charAt(0).toUpperCase()}.` : '';
    return initial ? `${family}, ${initial}` : family;
  }

  return family || given;
};

/**
 * Extracts a publication year from CSL date fields.
 */
export const getYear = (csl: Record<string, unknown>): string | undefined => {
  const issued = csl.issued as Record<string, unknown> | undefined;
  const dateParts = issued?.['date-parts'] as unknown;
  if (Array.isArray(dateParts) && Array.isArray(dateParts[0]) && dateParts[0][0]) {
    return String(dateParts[0][0]);
  }
  return normalizeText(issued?.raw || csl.year);
};

/**
 * Returns missing core metadata fields for bibliography review UI.
 */
export const buildMissingMetadata = (reference: Reference): string[] => {
  const csl = reference?.csl as Record<string, unknown> | undefined;
  if (!csl) {
    return ['author', 'year'];
  }

  const missingFields: string[] = [];
  if (getAuthors(csl).length === 0) {
    missingFields.push('author');
  }
  if (!getYear(csl)) {
    missingFields.push('year');
  }
  return missingFields;
};
